import request from '@/utils/request'

// ==================== 用户管理接口 ====================
// 基于 API文档-管理员系统模块.md 第7章节

/**
 * 查询所有用户列表
 * 接口地址: GET /users/list
 * @param {Object} params - 查询参数
 * @returns {Promise}
 */
export function getUserList(params) {
  return request({
    url: '/admin/users/list',
    method: 'get',
    params
  })
}

/**
 * 查看用户详情
 * 接口地址: GET /users/{userId}
 * @param {number} userId - 用户ID
 * @returns {Promise}
 */
export function getUserDetail(userId) {
  return request({
    url: `/admin/users/${userId}`,
    method: 'get'
  })
}

/**
 * 禁用/启用用户
 * 接口地址: PUT /users/{userId}/status
 * @param {number} userId - 用户ID
 * @param {Object} data - 请求数据 { is_active: boolean, reason?: string }
 * @returns {Promise}
 */
export function updateUserStatus(userId, data) {
  return request({
    url: `/admin/users/${userId}/status`,
    method: 'put',
    params: data  // 根据API文档，这里使用查询参数
  })
}

/**
 * 删除用户
 * 接口地址: DELETE /users/{userId}
 * @param {number} userId - 用户ID
 * @param {Object} data - 删除参数 { reason?: string }
 * @returns {Promise}
 */
export function deleteUser(userId, data) {
  return request({
    url: `/admin/users/${userId}`,
    method: 'delete',
    params: data  // 根据API文档，这里使用查询参数
  })
}

/**
 * 彻底删除用户及相关数据（硬删除，不可恢复）
 * 接口地址: DELETE /users/{userId}/permanent
 * @param {number} userId - 用户ID
 * @param {Object} params - 删除参数 { reason?: string, forceDelete?: boolean }
 * @returns {Promise}
 */
export function permanentDeleteUser(userId, params) {
  return request({
    url: `/admin/users/${userId}/permanent`,
    method: 'delete',
    params  // 根据API文档，这里使用查询参数
  })
}

/**
 * 修改用户会员等级
 * 接口地址: PUT /users/{userId}/membership
 * @param {number} userId - 用户ID
 * @param {Object} data - 请求数据 { membership_level_id: number, reason?: string }
 * @returns {Promise}
 */
export function updateUserMembership(userId, data) {
  return request({
    url: `/admin/users/${userId}/membership`,
    method: 'put',
    params: data  // 根据API文档，这里使用查询参数
  })
}

/**
 * 设置用户绑定门店
 * 接口地址: PUT /users/{userId}/bind-store
 * @param {number} userId - 用户ID
 * @param {Object} data - 请求数据 { store_id: number|null, reason?: string }
 * @returns {Promise}
 */
export function updateUserBindStore(userId, data) {
  return request({
    url: `/admin/users/${userId}/bind-store`,
    method: 'put',
    data  // 根据API文档，这里使用请求体
  })
}

// ==================== 辅助函数 ====================

/**
 * 获取用户状态选项
 * @returns {Array}
 */
export function getUserStatusOptions() {
  return [
    { value: true, label: '启用', type: 'success' },
    { value: false, label: '禁用', type: 'danger' }
  ]
}

/**
 * 获取性别选项
 * @returns {Array}
 */
export function getGenderOptions() {
  return [
    { value: 0, label: '未知' },
    { value: 1, label: '男' },
    { value: 2, label: '女' }
  ]
}

/**
 * 格式化手机号显示
 * @param {string} phone - 手机号
 * @returns {string}
 */
export function formatPhone(phone) {
  if (!phone) return ''
  return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
}

/**
 * 格式化会员折扣率显示
 * @param {number} discountRate - 折扣率（0.6表示6折）
 * @returns {string}
 */
export function formatDiscountRate(discountRate) {
  if (!discountRate) return '无折扣'
  return `${(discountRate * 10).toFixed(1)}折`
}

/**
 * 获取会员等级颜色
 * @param {number} levelId - 会员等级ID 
 * @param {number} sortOrder - 排序序号（可选，用于动态颜色）
 * @returns {string}
 */
export function getMembershipLevelColor(levelId, sortOrder = null) {
  // 如果有排序序号，使用动态颜色
  if (sortOrder !== null) {
    const colors = [
      '#909399', // 灰色 - 普通等级
      '#CD7F32', // 青铜色 - 青铜等级
      '#C0C0C0', // 银色 - 白银等级  
      '#FFD700', // 金色 - 黄金等级
      '#E5E4E2', // 铂金色 - 铂金等级
      '#9932CC', // 紫色 - 钻石等级
      '#FF1493', // 深粉色 - 至尊等级
      '#00CED1'  // 深青色 - 传奇等级
    ]
    return colors[sortOrder - 1] || colors[colors.length - 1]
  }
  
  // 兼容原有的ID映射（向后兼容）
  const colorMap = {
    1: '#909399', // 普通用户
    2: '#CD7F32', // 青铜会员
    3: '#C0C0C0', // 白银会员
    4: '#FFD700', // 黄金会员
    5: '#E5E4E2'  // 铂金会员
  }
  return colorMap[levelId] || '#409EFF'
} 